// Getting started: the same steps This PC walks through in the companion,
// for the people who read before they install, split by role (F210,
// decision 047; the words made plain in F512, decision 118). The download
// is a button naming the companion the platform calls the latest (decision 127).

import { createFileRoute } from "@tanstack/react-router";

import { External } from "@/components/External";
import { GetTheApp } from "@/components/GetTheApp";
import { useReleaseLink } from "@/host/links";

/** The source's releases page (the administrator's view; the pilots download from the platform). */
export const RELEASES_URL = "https://github.com/GianlucaBelardinelli/sharedairframe/releases";

export const Route = createFileRoute("/guide")({
  component: Guide,
});

function Guide() {
  const plugin = useReleaseLink("plugin.zip");
  return (
    <div className="space-y-10">
      <section className="space-y-3 pt-4">
        <h1 className="text-3xl font-semibold tracking-tight">Getting started</h1>
        <p className="muted max-w-2xl">
          Three parts, installed once. The website keeps each aircraft&apos;s state and logbook. The app on your PC moves the aircraft in and out of X-Plane. The plugin shows who has it and
          draws the wear in the cockpit. The Hot Start Challenger 650 is the only aircraft for now.
        </p>
      </section>

      <Section id="install" number={1} title="Install, everyone">
        <Step title="The app">
          <GetTheApp heading="Get the app on your PC" />
        </Step>
        <Step title="The plugin">
          Press the button on Set up with X-Plane closed: the app fetches the newest plugin and puts it into every X-Plane on this PC. If you prefer to do it by hand,{" "}
          <External href={plugin} className="underline">
            the plugin zip
          </External>{" "}
          unzips its <code className="mono">SharedAirframe</code> folder into <code className="mono">X-Plane 12\Resources\plugins</code>. The plugin shows who has the aircraft, draws the dirt
          in the cockpit, and tells the app when you fly.
        </Step>
        <Step title="Sign in">Press Sign in with Discord in the app. Your browser opens here, you approve once, and the app is signed in. The same Discord account signs you in on this site.</Step>
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section id="owner" number={2} title="Owners: share your airframe" testId="guide-owner">
          <Step title="Share it">
            On the Fleet, press Share an airframe. Choose who may fly it: just you for now, friends with a link, anyone who asks (you approve), or anyone signed in. The dialog ends with the
            invite link, copied with one click. The aircraft goes up with the sharing, X-Plane closed, so a member can book it and fly at once.
          </Step>
          <Step title="Invite">
            Send the link. The airframe&apos;s page has Invite a pilot for more links, and the Members tab lists the requests that come in, for you to approve or decline. Every member then
            sees the airframe in their app.
          </Step>
          <Step title="Keep an eye on it">
            The airframe&apos;s page shows who has it. The logbook lists every flight, the technical log every return. Change the name, who sees it and who may join from Settings whenever
            you like.
          </Step>
        </Section>

        <Section id="pilot" number={3} title="Pilots: fly a shared airframe" testId="guide-pilot">
          <Step title="Join">Open the invite link, or go to the airframe&apos;s page and press Ask to join. Once you are in, the airframe shows up in your app.</Step>
          <Step title="Book the aircraft">
            With X-Plane closed, press Book the aircraft. The app takes it for you and installs its latest state into your X-Plane, with a backup of what was there. Nobody else can fly it
            until you are done. The airframe&apos;s page then lists your flight step by step and ticks the steps as you go.
          </Step>
          <Step title="Sign the pre-flight">
            Open the pre-flight from the airframe&apos;s page. It shows what the record says about the aircraft: the state installed, whether it was left parked and shut down, the open
            defects, the deferrals, the failures, the dirt, and the note the previous pilot left you. Sign it, with a remark if you like. The signature stays on the booking and on the history.
          </Step>
          <Step title="Fly, then close X-Plane">
            Start X-Plane and fly. When you are parked and shut down, close X-Plane. The app waits for the files to settle, uploads the aircraft and writes the flight into the logbook. The
            next pilot starts from where you left it.
          </Step>
          <Step title="Write the handover">
            After the upload, write a few lines for the next pilot: how the aircraft behaved, what you noticed, where you left it, what is in the tanks. They read them on their pre-flight.
          </Step>
          <Step title="Watch a flight">
            While someone else flies the aircraft, press Watch the flight. Their state is installed on your PC read-only and the wear of their flight lands in your cockpit as it happens.
            Nothing to book or return on your side.
          </Step>
        </Section>
      </div>

      <Section id="maintenance" number={4} title="How the aircraft wears, and how it is kept" testId="guide-maintenance">
        <Step title="Every return is judged">
          When you return the aircraft, the platform checks the flight against the aircraft&apos;s own limits: how hard you landed for the weight, how fast you flew with the flaps and the
          gear out, how hot the engines ran and how they were started, how the brakes were used, whether you cooled the engines before shutdown. Then it writes what a crew would write in
          the technical log: a note, a warning that stays open until someone acknowledges it or a job closes it, or an inspection due that grounds the aircraft until the job is done. The
          Maintenance tab is the log. Every flight&apos;s page carries the sheet of its sector, with the fuel on board at departure and arrival and the uplift before the flight.
        </Step>
        <Step title="The aircraft's own recorder">
          The app sends the aircraft&apos;s flight data recorder with the return. From it the platform fills a card for every system with the last flight&apos;s figures and the trend over the
          last ten. A quantity drifting towards its limit becomes a watch item before it gets there: a reservoir losing fluid, the oxygen bottle emptying. A hot start is an inspection;
          reverse thrust below 60 knots is a note. The cards fill after your first flight with the plugin running.
        </Step>
        <Step title="What the plugin measures, what the platform models">
          The plugin reads the aircraft&apos;s own values: the oil servicing tank and the oxygen bottle when the aircraft loads, every ten minutes, and at the end. The aircraft does not expose
          the engines&apos; oil level, so the platform keeps one the way a real engine does: it burns a little every hour, far more with the aircraft&apos;s own burning-oil failure, and the
          pump puts back what you add. The batteries lose charge while the aircraft stands. No value inside the aircraft&apos;s files is ever changed.
        </Step>
        <Step title="Faults, as the aircraft fails">
          The platform knows the aircraft&apos;s own failure library and rolls the dice at every return, over the hours, the days and the landings flown, with worse odds for a component that
          has an open inspection. A part gets weak before it dies: a weak pump or a slow leak comes first, and a weak part left alone is ten times likelier to fail for good. A fault you set
          from the aircraft&apos;s own failure menu is recognised at the return too. The app sets a scheduled fault at the next load through the aircraft&apos;s own failure system, so the EICAS
          and the systems react exactly as they do to the menu. Where the MEL allows flying with the fault, the entry names the item, and the owner or a captain can defer it for the
          item&apos;s interval.
        </Step>
        <Step title="The jobs, on a parked aircraft">
          The pilot who has the aircraft runs the jobs from the Maintenance tab, with the aircraft parked and shut down. Each job takes time, and the aircraft is not to be flown meanwhile.
          The jobs are the cleanings, a tyre or brake change, a gear or engine inspection, a battery service, the airframe check, and a repair per chapter, which clears its faults at the
          next load. The shop takes the long ones: the landing gear goes away for overhaul, and an engine comes off the wing and returns with its life-limited parts new. Four jobs are done
          on the aircraft itself at the next load, through its own panels, and read back: the oil topped up, the oxygen bottle refilled, the fire bottles serviced, the air-driven generator
          restowed.
        </Step>
        <Step title="The programme">
          The programme lists the aircraft&apos;s scheduled inspections and when each falls due, from a few hundred hours to many years apart, the landing gear&apos;s restoration and overhaul
          among them. Each counts from its own last completion. The airframe check does every item due or nearly due; the gear overhaul and the engine shop visit cover theirs. The
          overview shows the item that is furthest along.
        </Step>
        <Step title="Your own lines on the sheet">
          The pilot who has the aircraft can write a defect or an observation on the sheet of the last sector, in their own words. A defect is a warning for maintenance; an observation is
          for the record. The sheet names who wrote it. Your line never grounds the aircraft and never changes the failure odds: the platform judges from what it measures, not from what we
          say.
        </Step>
      </Section>

      <section className="card space-y-2">
        <h2 className="text-lg font-semibold">What never happens</h2>
        <p className="muted text-sm">
          Nothing in your aircraft&apos;s files is ever modified. The app copies whole airframe folders, only while X-Plane is closed, and keeps a backup of every booking. If a return cannot be sent, it
          waits among the returns waiting and nothing is lost.
        </p>
      </section>
    </div>
  );
}

function Section({ id, number, title, testId, children }: { id: string; number: number; title: string; testId?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="card space-y-4" data-testid={testId}>
      <h2 className="flex items-center gap-3 text-lg font-semibold">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-accent text-sm text-accent tnum" aria-hidden="true">
          {number}
        </span>
        {title}
      </h2>
      <ol className="space-y-3">{children}</ol>
    </section>
  );
}

function Step({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <li className="space-y-1">
      <h3 className="font-semibold">{title}</h3>
      <div className="muted text-sm leading-relaxed">{children}</div>
    </li>
  );
}
